"use client";

/**
 * @module Checkbox
 *
 * Toggle control for binary choices. Built on Radix UI Checkbox primitive.
 *
 * @see {@link https://ui.shadcn.com/docs/components/checkbox Shadcn Checkbox}
 * @see {@link https://www.radix-ui.com/primitives/docs/components/checkbox Radix Checkbox}
 *
 * @example
 * // Basic checkbox
 * <Checkbox id="terms" />
 * <label htmlFor="terms">Accept terms</label>
 *
 * @example
 * // Controlled checkbox
 * const [checked, setChecked] = useState(false);
 * <Checkbox checked={checked} onCheckedChange={setChecked} />
 *
 * @example
 * // Rounded variant
 * <Checkbox variant="rounded" />
 */
import { cva } from "class-variance-authority";
import { Check } from "lucide-react";
import { Checkbox as CheckboxPrimitive } from "radix-ui";
import type * as React from "react";
import type { Ref } from "react";
import { cn } from "../lib/utils";

/** CVA variants for checkbox styling */
const checkboxVariants = cva("peer h-4 w-4 shrink-0 cursor-pointer rounded border border-gray-200 border-solid bg-transparent ring-offset-white hover:bg-gray-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-brand-primary data-[state=checked]:bg-brand-primary data-[state=checked]:text-white data-[state=checked]:hover:border-brand-primary-hover data-[state=checked]:hover:bg-brand-primary-hover dark:border-gray-700 dark:ring-offset-gray-950 dark:data-[state=checked]:border-brand-primary dark:data-[state=checked]:bg-brand-primary dark:data-[state=checked]:hover:border-brand-primary-hover dark:data-[state=checked]:hover:bg-brand-primary-hover dark:focus-visible:ring-[var(--focus-ring)] dark:hover:bg-gray-900", {
  variants: {
    variant: {
      default: "",
      rounded: "rounded-full dark:border-gray-500 [&[data-state='checked']]:border-brand-primary [&[data-state='checked']]:bg-brand-primary",
      // Radio, for single-choice groups: an outline ring with a filled dot,
      // never a tick — a tick reads as "included" next to a feature list.
      radio: "rounded-full border-gray-300 bg-transparent hover:bg-transparent dark:border-gray-300 dark:bg-transparent dark:hover:bg-transparent [&[data-state='checked']]:border-brand-primary [&[data-state='checked']]:bg-transparent [&[data-state='checked']]:hover:bg-transparent dark:[&[data-state='checked']]:bg-transparent dark:[&[data-state='checked']]:hover:bg-transparent [&[data-state='checked']]:text-brand-primary flex items-center justify-center"
    }
  }
});

/**
 * Props for the Checkbox component.
 * @property variant - Visual style: `"default"` (square), `"rounded"` (circular) or `"radio"`
 */
interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  ref?: Ref<React.ElementRef<typeof CheckboxPrimitive.Root>>;
  variant?: "default" | "rounded" | "radio";
}

/**
 * Interactive checkbox with check indicator.
 * Supports checked, unchecked, and indeterminate states.
 */
const Checkbox = ({
  className,
  variant = "default",
  ref,
  ...props
}: CheckboxProps) => <CheckboxPrimitive.Root className={cn(checkboxVariants({
  variant
}), className)} ref={ref} {...props} data-sentry-element="CheckboxPrimitive.Root" data-sentry-component="Checkbox" data-sentry-source-file="checkbox.tsx">
    <CheckboxPrimitive.Indicator className={cn("flex items-center justify-center text-current")} data-sentry-element="CheckboxPrimitive.Indicator" data-sentry-source-file="checkbox.tsx">
      <Check className="h-3 w-3" strokeWidth={3} data-sentry-element="Check" data-sentry-source-file="checkbox.tsx" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>;

/**
 * Props for the DummyCheckbox component.
 * @property variant - Visual style: `"default"`, `"rounded"` or `"radio"`
 * @property checked - Whether to show the check indicator
 */
interface DummyCheckboxProps extends React.ComponentPropsWithoutRef<"div"> {
  checked?: boolean;
  ref?: Ref<HTMLDivElement>;
  variant?: "default" | "rounded" | "radio";
}

/**
 * Non-interactive checkbox for display purposes only.
 * Visually identical to Checkbox but without click/keyboard handling.
 * Useful for list items where the parent handles selection.
 */
const DummyCheckbox = ({
  className,
  variant = "default",
  checked,
  ref,
  ...props
}: DummyCheckboxProps) => <div className={cn(checkboxVariants({
  variant
}), "flex items-center justify-center", className)} data-state={checked ? "checked" : "unchecked"} ref={ref} {...props} data-sentry-component="DummyCheckbox" data-sentry-source-file="checkbox.tsx">
    <div className={cn(checked ? "flex items-center justify-center text-current" : "hidden")}>
      {variant === "radio" ? <span className="size-2 rounded-full bg-current" /> : <Check className="h-3 w-3" strokeWidth={3} />}
    </div>
  </div>;
export { Checkbox, type CheckboxProps, DummyCheckbox };